import React from "react";
import classNames from "classnames/bind";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styles from "../authorization.module.scss";
import { EmailIcon, LockIcon, EyeIcon, GoogleIcon } from "../AuthIcons";
import { PasswordStrengthBar } from "../PasswordStrengthBar";

const cx = classNames.bind(styles);

const isGoogleOAuthEnabled = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === "true";

interface SignUpViewProps {
  email: string;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  emailError: boolean;
  passwordError: boolean;
  confirmPasswordError: boolean;
  authError: string | null;
  isLoading: boolean;
  passwordStrength: number;
  passwordStrengthColor: string;
  passwordStrengthText: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onSwitchToSignIn: () => void;
  onGoogleSignIn: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export const SignUpView: React.FC<SignUpViewProps> = ({
  email,
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  emailError,
  passwordError,
  confirmPasswordError,
  authError,
  isLoading,
  passwordStrength,
  passwordStrengthColor,
  passwordStrengthText,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onSwitchToSignIn,
  onGoogleSignIn,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className={cx('form')} onSubmit={onSubmit}>
      <div className={cx('formHeader')}>
        <h2 className={cx('formTitle')}>{t("createAccount")}</h2>
        <p className={cx('formSubtitle')}>{t("fillInDetails")}</p>
      </div>

      {authError && <div className={cx('errorMessage')}>{t(authError)}</div>}

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("email")}</label>
        <div className={cx('inputWrapper', { inputError: emailError })}>
          <div className={cx('inputIcon')}>
            <EmailIcon />
          </div>
          <input
            name="email"
            type="email"
            className={cx('input')}
            placeholder={t("enterEmail")}
            value={email}
            autoComplete="username"
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </div>
        {emailError && <span className={cx('inputErrorText')}>{t("enterEmail")}</span>}
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("password")}</label>
        <div className={cx('inputWrapper', { inputError: passwordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("enterPassword")}
            value={password}
            autoComplete="new-password"
            onChange={(e) => onPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onTogglePassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>
        {passwordError && <span className={cx('inputErrorText')}>{t("enterPassword")}</span>}
        {password && (
          <PasswordStrengthBar
            strength={passwordStrength}
            strengthColor={passwordStrengthColor}
            strengthText={passwordStrengthText}
          />
        )}
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("confirmPassword")}</label>
        <div className={cx('inputWrapper', { inputError: confirmPasswordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("confirmYourPassword")}
            value={confirmPassword}
            autoComplete="new-password"
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onToggleConfirmPassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showConfirmPassword} />
          </button>
        </div>
        {confirmPasswordError && <span className={cx('inputErrorText')}>{t("passwordsDoNotMatch")}</span>}
      </div>

      <button type="submit" className={cx('submitButton')} disabled={isLoading}>
        {isLoading ? <span className={cx('spinner')}></span> : t("signUp")}
      </button>

      <p className={cx('consentText')}>
        <Trans
          i18nKey="registrationConsent.text"
          components={{
            terms: <Link to="/agb" target="_blank" rel="noopener noreferrer" />,
            privacy: <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" />,
          }}
        />
      </p>

      {isGoogleOAuthEnabled && (
        <>
          <div className={cx('divider')}>
            <span>{t("or")}</span>
          </div>

          <button
            type="button"
            className={cx('googleButton')}
            onClick={onGoogleSignIn}
            disabled={isLoading}
          >
            <GoogleIcon />
            <span>{t("signUpWithGoogle")}</span>
          </button>
        </>
      )}

      <div className={cx('formFooter')}>
        <span className={cx('footerText')}>{t("alreadyHaveAccount")}</span>
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className={cx('switchButton')}
        >
          {t("signIn")}
        </button>
      </div>
    </form>
  );
};
